import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import { Box, Col, Link, Row, Text } from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import type {
  ILpComponentNarrativeLockups,
  ILpElementNarrativeLockup,
} from 'declarations/contentful';

import Container from './shared/Container';

const LOCKUP_ROW_DESKTOP = { flexDirection: 'row', gap: '$24x' } as const;
const LOCKUP_HEADER_DESKTOP = { flexBasis: 320, flexShrink: 0 } as const;
const SECTION_TITLE_MOBILE = { typography: '$headerSmall' } as const;

const hasText = (node: unknown): boolean => {
  if (!node || typeof node !== 'object') {
    return false;
  }

  const richTextNode = node as { value?: unknown; content?: unknown[] };

  if (typeof richTextNode.value === 'string' && richTextNode.value.trim()) {
    return true;
  }

  return richTextNode.content?.some((child) => hasText(child)) ?? false;
};

const isEmptyRichText = (document?: Document) => {
  if (!document?.content?.length) {
    return true;
  }

  return !document.content.some((node) => hasText(node));
};

const richTextOptions = (analyticsId?: string): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text tag="p" typography="$bodyLarge" color="$contentPrimary" display="block">
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Text tag="h3" typography="$subheaderMedium" color="$contentPrimary" display="block">
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Text tag="h4" typography="$subheaderSmall" color="$contentPrimary" display="block">
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Col tag="ul" gap="$3x" paddingLeft="$10x">
        {children}
      </Col>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <Col tag="ol" gap="$3x" paddingLeft="$10x">
        {children}
      </Col>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => <Col tag="li">{children}</Col>,
    [BLOCKS.QUOTE]: (_, children) => (
      <Row
        tag="blockquote"
        gap="$4x"
        paddingLeft="$6x"
        borderLeftWidth={3}
        borderLeftColor="$borderPrimary"
      >
        <Col gap="$4x">{children}</Col>
      </Row>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link
        href={node.data.uri}
        variant="primary"
        analyticsName={`cosmos-landing-page-v2-narrative-lockups-${analyticsId ?? 'link'}`}
      >
        {children}
      </Link>
    ),
  },
});

const NarrativeLockup = ({
  lockup,
  analyticsId,
  showTopline,
}: {
  lockup: ILpElementNarrativeLockup;
  analyticsId?: string;
  showTopline: boolean;
}) => {
  const { eyebrow, title, body } = lockup.fields;

  if (!eyebrow && !title && (!body || isEmptyRichText(body))) {
    return null;
  }

  return (
    <Box
      flexDirection="column"
      gap="$10x"
      paddingTop={showTopline ? '$14x' : 0}
      borderTopWidth={showTopline ? 1 : 0}
      borderTopColor="$borderPrimary"
      $largerThanMD={LOCKUP_ROW_DESKTOP}
    >
      <Col gap="$3x" $largerThanMD={LOCKUP_HEADER_DESKTOP}>
        {eyebrow && (
          <Text
            tag="p"
            typography="$labelXsmall"
            fontWeight="$medium"
            color="$contentTertiary"
            textTransform="uppercase"
            display="block"
          >
            {eyebrow}
          </Text>
        )}
        {title && (
          <Text tag="h2" typography="$headerSmall" color="$contentPrimary" display="block">
            {title}
          </Text>
        )}
      </Col>
      {body && !isEmptyRichText(body) && (
        <Col gap="$8x" flex={1} maxWidth={880}>
          {documentToReactComponents(body, richTextOptions(analyticsId))}
        </Col>
      )}
    </Box>
  );
};

export const RenderNarrativeLockups = (entry: ILpComponentNarrativeLockups) => {
  const { analyticsId, eyebrow, title, intro, items } = entry.fields;
  const showTopline = entry.fields.showTopline ?? true;

  if (!items?.length) {
    return null;
  }

  return (
    <Container noSectionSpacing paddingVertical="$20x">
      <Col gap="$18">
        {(eyebrow || title || (intro && !isEmptyRichText(intro))) && (
          <Col gap="$4x" maxWidth="$200x">
            {eyebrow && (
              <Text
                tag="p"
                typography="$labelXsmall"
                fontWeight="$medium"
                color="$contentTertiary"
                textTransform="uppercase"
                display="block"
              >
                {eyebrow}
              </Text>
            )}
            {title && (
              <Text
                tag="h2"
                typography="$headerMedium"
                color="$contentPrimary"
                display="block"
                $smallerThanMD={SECTION_TITLE_MOBILE}
              >
                {title}
              </Text>
            )}
            {intro && !isEmptyRichText(intro) && (
              <Col gap="$4x">{documentToReactComponents(intro, richTextOptions(analyticsId))}</Col>
            )}
          </Col>
        )}
        <Col gap="$18">
          {items.map((lockup, index) => (
            <NarrativeLockup
              key={lockup.sys?.id ?? index}
              lockup={lockup}
              analyticsId={analyticsId}
              showTopline={showTopline}
            />
          ))}
        </Col>
      </Col>
    </Container>
  );
};

const NarrativeLockups: EntryComponent<ILpComponentNarrativeLockups> = {
  render: RenderNarrativeLockups,
};

export default NarrativeLockups;
